import { left } from "@popperjs/core";
import Overlay from "@shared/ui/Overlay";
import { useRef, useState } from "react";

export const TAG_COLORS = {
  PINK: "pink",
  VIOLET: "violet",
  PURPLE: "purple",
  MINT: "mint",
  MINT_LIGHT: "mintLight",
  BROWN: "brown",
  GREEN: "green",
  RED: "red",
  ORANGE: "orange",
  BLUE: "blue",
  GREY: "grey",
  BLACK: "black",
} as const;

export type TagColorType = (typeof TAG_COLORS)[keyof typeof TAG_COLORS];

const COLOR_STYLES: Record<TagColorType, string> = {
  [TAG_COLORS.PINK]: "bg-pink-lightest text-pink",
  [TAG_COLORS.BLACK]: "bg-tertiary text-primary",
  [TAG_COLORS.VIOLET]: "bg-violet-lightest text-violet",
  [TAG_COLORS.PURPLE]: "bg-purple-lightest text-purple",
  [TAG_COLORS.MINT]: "bg-mint-light text-mintBlue",
  [TAG_COLORS.MINT_LIGHT]: "bg-mint-lightest text-mintBlue",
  [TAG_COLORS.BROWN]: "bg-brown-lightest text-brown",
  [TAG_COLORS.GREEN]: "bg-success-lighter text-success",
  [TAG_COLORS.RED]: "bg-danger-lightest text-danger",
  [TAG_COLORS.ORANGE]: "bg-warning-lightest text-warning",
  [TAG_COLORS.BLUE]: "bg-brand-lightest text-brand",
  [TAG_COLORS.GREY]: "bg-accent text-secondary",
};

const HOVER_COLOR_STYLES: Record<TagColorType, string> = {
  [TAG_COLORS.PINK]:
    "hover:bg-pink hover:text-inverse group-hover:bg-pink group-hover:text-inverse",
  [TAG_COLORS.BLACK]:
    "hover:bg-secondary hover:text-inverse group-hover:bg-secondary group-hover:text-inverse",
  [TAG_COLORS.VIOLET]:
    "hover:bg-violet hover:text-inverse group-hover:bg-violet group-hover:text-inverse",
  [TAG_COLORS.PURPLE]:
    "hover:bg-purple hover:text-inverse group-hover:bg-purple group-hover:text-inverse",
  [TAG_COLORS.MINT]:
    "hover:bg-mint hover:text-inverse group-hover:bg-mint group-hover:text-inverse",
  [TAG_COLORS.MINT_LIGHT]:
    "hover:bg-mint hover:text-inverse group-hover:bg-mint group-hover:text-inverse",
  [TAG_COLORS.BROWN]:
    "hover:bg-brown hover:text-inverse group-hover:bg-brown group-hover:text-inverse",
  [TAG_COLORS.GREEN]:
    "hover:bg-success hover:text-inverse group-hover:bg-success group-hover:text-inverse",
  [TAG_COLORS.RED]:
    "hover:bg-danger hover:text-inverse group-hover:bg-danger group-hover:text-inverse",
  [TAG_COLORS.ORANGE]:
    "hover:bg-warning hover:text-inverse group-hover:bg-warning group-hover:text-inverse",
  [TAG_COLORS.BLUE]:
    "hover:bg-brand-dark hover:text-inverse group-hover:bg-brand-dark group-hover:text-inverse",
  [TAG_COLORS.GREY]:
    "hover:bg-secondary hover:text-inverse group-hover:bg-secondary group-hover:text-inverse",
};

type TagPropsType = {
  label: string;
  color?: TagColorType;
  className?: string;
  isUpperCase?: boolean;
  isHoverable?: boolean;
  style?: React.CSSProperties;
  leadingIcon?: React.JSX.Element;
  trailingIcon?: React.JSX.Element;
  setRef?: (_: HTMLSpanElement) => void;
  Tooltip?: () => React.ReactNode;
  showTooltip?: boolean;
  tooltipBgColor?: string;
  allowOverlayHover?: boolean;
  reasonReferenceMap?: unknown;
} & React.HTMLAttributes<HTMLSpanElement>;

const Tag = ({
  label,
  color = null,
  className = "",
  isUpperCase = true,
  isHoverable = true,
  Tooltip = null,
  showTooltip = false,
  style = null,
  tooltipBgColor = "bg-primary",
  leadingIcon = null,
  trailingIcon = null,
  setRef,
  allowOverlayHover = false,
  reasonReferenceMap: _reasonReferenceMap,
  ...props
}: TagPropsType) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isOverlayHovered, setIsOverlayHovered] = useState<boolean>(false);

  const handleMouseEnter = () => setIsHovered(true);

  const handleMouseLeave = () => {
    if (!allowOverlayHover) {
      setIsHovered(false);
    } else {
      setTimeout(() => {
        if (!isOverlayHovered) {
          setIsHovered(false);
        }
      }, 100);
    }
  };

  const handleOverlayMouseEnter = () => {
    if (allowOverlayHover) {
      setIsOverlayHovered(true);
      setIsHovered(true);
    }
  };

  const handleOverlayMouseLeave = () => {
    if (allowOverlayHover) {
      setIsOverlayHovered(false);
      setIsHovered(false);
    }
  };

  const containerRef = useRef<HTMLSpanElement>(null);

  const shouldShowOverlay = !!(
    (isHovered || (allowOverlayHover && isOverlayHovered)) &&
    Tooltip &&
    showTooltip
  );

  return (
    <>
      <span
        className={`inline-flex items-center justify-center gap-2xs rounded h-2xl px-xs py-2xs transition-all duration-200 ${
          color ? COLOR_STYLES[color] : ""
        } ${
          isHoverable && color ? HOVER_COLOR_STYLES[color] : ""
        } ${className}`}
        style={
          style
            ? isHoverable && isHovered
              ? {
                  color: "#FFFFFF",
                  backgroundColor: style.color,
                }
              : style
            : null
        }
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={(ref) => {
          if (setRef) setRef(ref);
          containerRef.current = ref;
        }}
        {...props}
      >
        {leadingIcon}
        <span
          className={`font-manrope text-xs font-bold truncate ${
            isUpperCase ? "uppercase" : ""
          }`}
        >
          {label}
        </span>
        {trailingIcon}
      </span>
      {Tooltip && (
        <Overlay
          arrowClasses={`before:${tooltipBgColor} before:shadow-alert`}
          className={`border border-solid border-primary ${tooltipBgColor} max-w-72xl p-md rounded shadow-alert w-full z-50`}
          containerRef={document.body}
          offset={[0, 10]}
          placement={left}
          showOverlay={shouldShowOverlay}
          targetRef={containerRef}
        >
          <div
            onMouseEnter={
              allowOverlayHover ? handleOverlayMouseEnter : undefined
            }
            onMouseLeave={
              allowOverlayHover ? handleOverlayMouseLeave : undefined
            }
          >
            {<Tooltip />}
          </div>
        </Overlay>
      )}
    </>
  );
};

export default Tag;
